import React from 'react';
import { AudioButton } from './AudioButton';
import { MicrophoneButton } from './MicrophoneButton';
import { toast } from 'sonner';

/**
 * Shared PracticeButton used across all course pages.
 */
export const CoursePracticeButton = ({ text }) => (
  <div className="inline-flex items-center gap-1">
    <AudioButton text={text} size="sm" voice="nova" />
    <MicrophoneButton
      size="sm"
      expectedText={text}
      onTranscription={(t) => { if (t) toast.info(`You said: "${t}"`); }}
      onPronunciationScore={() => {}}
    />
  </div>
);

/**
 * Renders a backend-driven lesson tab with vocab, grammar, phrases, dialogs, tips, body grid.
 * Used by MenschenA1, A12, A21, A22, and PflegeTraining.
 */
export const CourseModuleRenderer = ({ tabId, moduleData, isActive, accentColor = '#3b82f6' }) => (
  <div className={`section ${isActive ? 'active' : ''}`} data-testid={`tab-${tabId}`}>
    <h2 style={{ fontSize: '22px', fontWeight: '700', marginBottom: '8px' }}>{moduleData.title.de}</h2>
    <p style={{ color: '#94a3b8', marginBottom: '20px' }}>{moduleData.title.en} · <span style={{ color: accentColor }}>{moduleData.title.ar}</span></p>

    {moduleData.sections.map((section, si) => {
      const sKey = `${tabId}-${section.type}-${si}`;

      if (section.type === 'vocab_table') {
        return (
          <div key={sKey} className="card">
            <div className="card-header">
              <span className="card-icon">{section.icon}</span>
              <h3>{section.title.de} · {section.title.en} · <span style={{ color: accentColor }}>{section.title.ar}</span></h3>
            </div>
            <div className="card-body">
              <table className="vocab-table">
                <thead><tr><th>Deutsch</th><th>English</th><th className="ar" style={{ color: accentColor }}>عربي</th>{section.hasAudio && <th>Üben</th>}</tr></thead>
                <tbody>
                  {section.items.map((item, i) => (
                    <tr key={`voc-${item.de?.slice(0, 10)}-${i}`}>
                      <td className="de">{item.de}</td>
                      <td className="en">{item.en}</td>
                      <td className="ar" style={{ color: accentColor }}>{item.ar}</td>
                      {section.hasAudio && <td><CoursePracticeButton text={item.de} /></td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      }

      if (section.type === 'grammar_table') {
        return (
          <div key={sKey} className="card">
            <div className="card-header">
              <span className="card-icon">{section.icon}</span>
              <h3>{section.title.de} · {section.title.en} · <span style={{ color: accentColor }}>{section.title.ar}</span></h3>
            </div>
            <div className="card-body">
              {section.subtitle?.de && <p style={{ color: '#94a3b8', fontSize: '13px', marginBottom: '12px' }}>{section.subtitle.de}</p>}
              <div className="grammar-box">
                <table className="grammar-table">
                  <thead><tr>{section.headers.map((h, hi) => <th key={`h-${h}-${hi}`} className={h === 'عربي' ? 'ar' : ''} style={h === 'عربي' ? { color: accentColor } : {}}>{h}</th>)}</tr></thead>
                  <tbody>
                    {section.rows.map((row, ri) => (
                      <tr key={`row-${ri}-${row[0]}`}>
                        {row.map((cell, ci) => (
                          <td key={`c-${ri}-${ci}`} className={ci === 1 ? 'em' : ci === row.length - 1 ? 'ar' : ''} style={ci === row.length - 1 ? { color: accentColor } : {}}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      }

      if (section.type === 'phrase_grid') {
        return (
          <div key={sKey} className="card">
            <div className="card-header">
              <span className="card-icon">{section.icon}</span>
              <h3>{section.title.de} · {section.title.en} · <span style={{ color: accentColor }}>{section.title.ar}</span></h3>
            </div>
            <div className="card-body">
              <div className="phrase-grid">
                {section.items.map((item, i) => (
                  <div key={`ph-${item.ctx}-${item.de?.slice(0, 8)}-${i}`} className="phrase-card">
                    <div className="phrase-ctx" style={{ color: accentColor }}>{item.ctx}</div>
                    <div className="phrase-de flex items-center justify-between">
                      <span>{item.de}</span>
                      <CoursePracticeButton text={item.de} />
                    </div>
                    <div className="phrase-en">{item.en}</div>
                    <div className="phrase-ar" style={{ color: accentColor }}>{item.ar}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      }

      if (section.type === 'body_grid') {
        return (
          <div key={sKey} className="card">
            <div className="card-header">
              <span className="card-icon">{section.icon}</span>
              <h3>{section.title.de} · {section.title.en} · <span style={{ color: accentColor }}>{section.title.ar}</span></h3>
            </div>
            <div className="card-body">
              <div className="body-grid">
                {section.items.map((item, i) => (
                  <div key={`bd-${item.de?.slice(0, 8)}-${i}`} className="body-item">
                    <div className="de">{item.de}</div>
                    <div className="en">{item.en}</div>
                    <div className="ar" style={{ color: accentColor }}>{item.ar}</div>
                    <div className="mt-1"><CoursePracticeButton text={item.de} /></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      }

      if (section.type === 'dialog') {
        return (
          <div key={sKey} className="dialog-box">
            <div className="dialog-title" style={{ color: accentColor }}>{section.title}</div>
            <div className="dialog-content">
              {section.lines.map((line, j) => (
                <div key={`dl-${line.speaker}-${j}`} className="dialog-line">
                  <div className="dialog-speaker" style={{ color: j % 2 === 0 ? '#14b8a6' : '#f59e0b' }}>{line.speaker}:</div>
                  <div className={`dialog-bubble ${j % 2 !== 0 ? 'patient' : ''}`}>
                    <div className="de" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>{line.de}</span>
                      <CoursePracticeButton text={line.de} />
                    </div>
                    <div className="en">{line.en}</div>
                    <div className="ar" style={{ color: accentColor }}>{line.ar}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      }

      if (section.type === 'tip') {
        return (
          <div key={sKey} className="tip-box" style={{ marginTop: '16px', background: `linear-gradient(135deg, ${accentColor}15, ${accentColor}08)`, border: `1px solid ${accentColor}4D` }}>
            <strong style={{ color: accentColor }}>Tipp · Tip · <span>نصيحة</span></strong><br />
            {section.text.de} · {section.text.en} · <span style={{ color: accentColor }}>{section.text.ar}</span>
          </div>
        );
      }

      return null;
    })}
  </div>
);
